import {
  collection,
  doc,
  query,
  serverTimestamp,
  type Timestamp,
  where,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
  collectionGroup,
} from 'firebase/firestore'
import { buildUnsupportedConverter } from '../shared/noops'
import { type FirestorePromptPostRoomStateAIEvent, schema } from './schema'
import { type PostRoomStatePromptType } from '../PromptPostRoomStateType/types'
import { type FirebaseRepository } from '../../models/FirebaseRepository'
import {
  collectionSnapshots,
  modelListStream,
} from '../../firestore-mobx/stream'
import { PromptPostRoomStateAIEvent } from '../../models/PromptPostRoomStateAIEvent'
import { addDocWithError, getDocWithError } from '../../firestore-mobx/fetch'
import { PromptType } from '../PromptVersion/types'

export * from './schema'

const unsupported =
  buildUnsupportedConverter<FirestorePromptPostRoomStateAIEvent>()

const readConverter: FirestoreDataConverter<FirestorePromptPostRoomStateAIEvent> =
  {
    toFirestore: unsupported.toFirestore,
    fromFirestore: (snapshot: QueryDocumentSnapshot) => {
      const data = snapshot.data({ serverTimestamps: 'estimate' })
      return schema.parse(data)
    },
  }

const getColRef = (repository: FirebaseRepository) =>
  collection(
    repository.firestore,
    'prompt',
    'post_room_state_prompt',
    'ai'
  ).withConverter(readConverter)

// todo: add compound index for promptType and promptVersionId on this collection
export const getPromptPostRoomStateAIEvents = (
  repository: FirebaseRepository,
  {
    promptType,
    promptVersionId,
  }: {
    promptType: PostRoomStatePromptType
    promptVersionId: string
  }
) => {
  const q = query(
    getColRef(repository),
    where('promptType', '==', promptType),
    where('promptVersionId', '==', promptVersionId)
  )
  return modelListStream(repository, q, PromptPostRoomStateAIEvent)
}

export const startPostRoomStatePromptTestForRoom = async (
  repository: FirebaseRepository,
  {
    promptType,
    promptVersionId,
    roomId,
  }: {
    promptType: PostRoomStatePromptType
    promptVersionId: string
    roomId: string
  }
) => {
  const colRef = getColRef(repository)

  await addDocWithError(
    colRef.withConverter(null),
    {
      endedAt: null,
      promptType,
      promptVersionId,
      roomId,
      startedAt: serverTimestamp(),
    },
    'StartPostRoomStatePromptTestForRoom'
  )

  return true
}

export const getResultsForPostRoomStatePromptTests = (
  repository: FirebaseRepository,
  {
    promptType,
    promptVersionId,
  }: {
    promptType: PostRoomStatePromptType
    promptVersionId: string
  }
) => {
  type Result = QueryDocumentSnapshot<{
    updatedAt: Timestamp
    roomId: string
    aIId: string
    confidenceScore?: number
  }>

  const resultsQuery = query(
    collectionGroup(repository.firestore, 'result'),
    where('aiResultsType', '==', promptType),
    where('promptVersionId', '==', promptVersionId)
  )

  return collectionSnapshots(resultsQuery).map(({ docs }) => docs as Result[])
}

// todo: we may want some schemas to parse the results into, will be unique for each post_room_state prompt type
export const fetchResultsForSuccessfulPostRoomStatePromptTest = async (
  repository: FirebaseRepository,
  {
    promptType,
    promptVersionId,
    roomId,
  }: {
    promptType: PostRoomStatePromptType
    promptVersionId: string
    roomId: string
  }
) => {
  const resultsRef = doc(
    repository.firestore,
    'prompt',
    PromptType.postRoomState,
    'type',
    promptType,
    'prompt_version',
    promptVersionId,
    'test',
    roomId
  )
  const snap = await getDocWithError(
    resultsRef,
    'fetchResultsForSuccessfulPostRoomStatePromptTest'
  )
  if (!snap.exists())
    throw new Error(
      `No results found for prompt type ${promptType}, version ${promptVersionId}, room ${roomId}`
    )
  return snap.data()
}
